import React, { useState, useMemo, useRef, useEffect } from 'react'
import { uuid } from 'uuidv4'
import dayjs from 'dayjs'
import groupBy from 'lodash/groupBy'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Button from 'antd/lib/button'
import Tooltip from 'antd/lib/tooltip'
import PageHeader from 'antd/lib/page-header'
import { PlusCircleOutlined } from '@ant-design/icons'
import { FaChevronUp, FaChevronDown } from 'react-icons/fa'
import SpecialDay from './SpecialDay'
import { SplitHour } from '../types'
import {
  StyledPastSO,
  StyledButton,
  StyledHeader,
  PastSOContainer
} from 'components/Common'

interface SpecialOpeningDatesProps {
  shifts: SplitHour[] | []
  disabled: boolean
  updateDatedShifts: (datedShifts: SplitHour[]) => void
}

const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_SHIFT_SHAPE = {
  day_of_week: 'monday',
  start_time: '08:00:00',
  seconds_length: 3600,
  enabled: true,
  special_date: null,
  closed: false
}

const SpecialOpeningDates = ({
  shifts,
  disabled,
  updateDatedShifts
}: SpecialOpeningDatesProps) => {
  const groupShiftsByDate = useMemo(
    () => (datedShifts: SplitHour[]) =>
      groupBy(datedShifts, ({ special_date }: SplitHour) =>
        dayjs(special_date).format(DATE_FORMAT)
      ),
    []
  )
  const contentRef = useRef(null)
  const threeMonthsAgo = dayjs().subtract(3, 'months')
  const specialDates = groupShiftsByDate(shifts)
  const [height, setHeight] = useState<number>(0)
  const [showPastThreeMonths, setShowPastThreeMonths] = useState<boolean>(false)

  useEffect(() => {
    if (contentRef.current) {
      // @ts-ignore
      setHeight(contentRef.current.scrollHeight)
    }
  }, [showPastThreeMonths])

  const addSpecialDate = () => {
    const formattedDate = dayjs().format(DATE_FORMAT)
    const editingDateShifts = [...(specialDates[formattedDate] || [])]

    const newDatedShifts = [
      ...editingDateShifts,
      {
        ...DEFAULT_SHIFT_SHAPE,
        day_of_week: null,
        special_date: null
      }
    ]
    updateDatedShifts([...shifts, ...newDatedShifts] as SplitHour[])
  }

  const updateSpecialDayShiftsState = (
    dateStamp: string,
    newShifts: SplitHour[]
  ) => {
    const updatingDatedShifts = shifts.filter(
      ({ special_date }: SplitHour) => special_date !== dateStamp
    )
    updateDatedShifts([...updatingDatedShifts, ...newShifts])
  }

  const removeDatedShifts = (dateStamp: string) => {
    const newDatedShifts = shifts.filter(
      ({ special_date }: SplitHour) => special_date !== dateStamp
    )

    updateDatedShifts(newDatedShifts)
  }

  const AddSpecialSettingButton = () => {
    const isAddButtonDisabled =
      disabled ||
      Object.keys(specialDates).some((date) => date === 'Invalid date')
    const buttonText = 'Add special opening time'
    return isAddButtonDisabled ? (
      <Tooltip placement='right' title={'Please select a date first.'}>
        <Button
          disabled={isAddButtonDisabled}
          onClick={addSpecialDate}
          icon={<PlusCircleOutlined />}
        >
          {buttonText}
        </Button>
      </Tooltip>
    ) : (
      <Button
        disabled={isAddButtonDisabled}
        onClick={addSpecialDate}
        icon={<PlusCircleOutlined />}
      >
        {buttonText}
      </Button>
    )
  }

  const Header = () => (
    <Row>
      <Col span={12} />
      <Col span={12}>
        <Row className='_mb-0' gutter={[8, 0]}>
          <Col span={11}>Start</Col>
          <Col span={11}>End</Col>
        </Row>
      </Col>
    </Row>
  )

  return (
    <>
      <Row className='_mb-0'>
        <Col span={12}>
          <PageHeader
            title='Special Opening Hours'
            className='settings-title'
          />
        </Col>
        <Col span={12}>
          <Row className='_mb-0' justify='end'>
            <Col>
              <AddSpecialSettingButton />
            </Col>
            <Col span={2} />
          </Row>
        </Col>
      </Row>

      {!!Object.keys(specialDates).length && <Header />}
      {Object.keys(specialDates)
        .filter((date) => date === 'Invalid date')
        .map((date: string) => (
          <SpecialDay
            key={uuid()}
            dateStamp={date}
            disabled={disabled}
            shifts={specialDates[date]}
            usedDates={Object.keys(specialDates)}
            removeDatedShifts={removeDatedShifts}
            updateSpecialDayShifts={updateSpecialDayShiftsState}
          />
        ))}
      {Object.keys(specialDates)
        .filter((date) => dayjs(date) > threeMonthsAgo)
        .map((date: string) => (
          <SpecialDay
            key={uuid()}
            dateStamp={date}
            disabled={disabled}
            shifts={specialDates[date]}
            usedDates={Object.keys(specialDates)}
            removeDatedShifts={removeDatedShifts}
            updateSpecialDayShifts={updateSpecialDayShiftsState}
          />
        ))}
      <Row className='_mb-4'>
        <Col span={12}>
          <StyledHeader>Past Special Opening Hours</StyledHeader>
        </Col>
        <Col span={12}>
          <StyledButton
            onClick={() => setShowPastThreeMonths(!showPastThreeMonths)}
          >
            <Row justify='end' align='middle' className='_mb-0' gutter={8}>
              {showPastThreeMonths ? (
                <>
                  <Col>Hide Past Opening Hours</Col>
                  <Col>
                    <Row className='_mb-0' justify='end'>
                      <FaChevronUp />
                    </Row>
                  </Col>
                  <Col span={2} />
                </>
              ) : (
                <>
                  <Col>See Past Opening Hours</Col>
                  <Col>
                    <Row className='_mb-0' justify='end'>
                      <FaChevronDown />
                    </Row>
                  </Col>
                  <Col span={2} />
                </>
              )}
            </Row>
          </StyledButton>
        </Col>
      </Row>
      <Row>
        <StyledPastSO>
          Past special opening hours will automatically be deleted after 3
          months. You can always refer to them before the period ends.
        </StyledPastSO>
      </Row>
      <Row>
        <Col span={24}>
          <PastSOContainer isOpen={showPastThreeMonths} height={height}>
            <div ref={contentRef}>
              {Object.keys(specialDates)
                .filter((date) => dayjs(date) < threeMonthsAgo)
                .map((date: string) => (
                  <SpecialDay
                    key={uuid()}
                    dateStamp={date}
                    disabled={disabled}
                    shifts={specialDates[date]}
                    usedDates={Object.keys(specialDates)}
                    removeDatedShifts={removeDatedShifts}
                    updateSpecialDayShifts={updateSpecialDayShiftsState}
                  />
                ))}
            </div>
          </PastSOContainer>
        </Col>
      </Row>
    </>
  )
}

export default SpecialOpeningDates
